import React from "react";
import { IoCloseOutline } from "react-icons/io5";

interface Account {
  type: "user" | "staff" | "parent" | string;
  email: string;
  roles?: string[];
  centerName?: string;
}

interface AccountTypeModalProps {
  isOpen: boolean;
  onClose: () => void;
  accounts: Account[];
  onSelectAccountType: (accountType: string) => void;
  isLoading?: boolean;
  description?: string;
}

const AccountTypeModal: React.FC<AccountTypeModalProps> = ({
  isOpen,
  onClose,
  accounts,
  onSelectAccountType,
  isLoading = false,
  description,
}) => {
  const modalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getAccountLabel = (account: Account) => {
    if (account.type === "user") {
      const role = account.roles?.[0] ?? "User";
      return `${role} (Childcare Forward)`;
    }
    const labels: { [key: string]: string } = {
      staff: "Staff",
      parent: "Parent",
      center: "Center",
    };
    const typeLabel = labels[account.type] || account.type;
    return account.centerName ? `${typeLabel} - ${account.centerName}` : typeLabel;
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 h-screen py-3 px-3">
      <div
        ref={modalRef}
        className="bg-white p-8 rounded-lg shadow-lg w-full max-w-[500px]"
      >
        <div className="flex items-start justify-between mb-6">
          <h2 className="text-2xl md:text-3xl text-secondary font-semibold">
            Select Account Type
          </h2>
          <button
            onClick={onClose}
            className="text-secondary hover:text-red-500 transition-colors"
            disabled={isLoading}
          >
            <IoCloseOutline size={28} />
          </button>
        </div>

        <p className="text-base text-secondaryVariant mb-6">
          {description ?? "Multiple accounts found with these credentials. Please select which account you'd like to log in to:"}
        </p>

        <div className="space-y-3">
          {accounts.map((account, index) => (
            <button
              key={index}
              onClick={() => onSelectAccountType(account.type)}
              disabled={isLoading}
              className="w-full p-4 text-left border border-secondaryVariant rounded-lg hover:bg-sky hover:border-secondary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex flex-col">
                <span className="text-base font-semibold text-secondary">
                  {getAccountLabel(account)}
                </span>
                <span className="text-sm text-secondaryVariant">
                  {account.email}
                </span>
              </div>
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="mt-4 flex justify-center">
            <div className="animate-spin h-5 w-5 border-2 border-secondary border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountTypeModal;
